import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, X, Calendar, Bell, Users } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import axios from "axios";
import { toast } from "sonner";

const LABEL_COLORS = [
  "#10b981", // green
  "#3b82f6", // blue
  "#f59e0b", // yellow
  "#ef4444", // red
  "#8b5cf6", // purple
  "#ec4899", // pink
];

export default function CardModal({ card, boardId, onClose, onUpdate, onDelete }) {
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description || "");
  const [labels, setLabels] = useState(card.labels || []);
  const [dueDate, setDueDate] = useState(card.dueDate ? new Date(card.dueDate) : null);
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [labelText, setLabelText] = useState("");
  const [labelColor, setLabelColor] = useState(LABEL_COLORS[0]);
  const [reminderDate, setReminderDate] = useState(null);
  const [boardMembers, setBoardMembers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState(card.assignedUsers || []);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [boardOwnerId, setBoardOwnerId] = useState(null);

  useEffect(() => {
    fetchBoardMembers();
    fetchCurrentUser();
    fetchBoardInfo();
  }, [boardId]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get("/auth/me");
      setCurrentUserId(response.data.id);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchBoardInfo = async () => {
    try {
      const response = await axios.get(`/boards/${boardId}`);
      setBoardOwnerId(response.data.ownerId);
    } catch (error) {
      console.error("Error fetching board info:", error);
    }
  };

  const fetchBoardMembers = async () => {
    try {
      const response = await axios.get(`/boards/${boardId}/members`);
      setBoardMembers(response.data);
    } catch (error) {
      console.error("Error fetching board members:", error);
    }
  };

  const handleSave = () => {
    onUpdate(card.id, {
      title,
      description,
      labels,
      dueDate: dueDate ? dueDate.toISOString() : null,
      assignedUsers,
    });
    onClose();
  };

  const toggleUserAssignment = (userId) => {
    // Only board owner can assign users
    if (currentUserId !== boardOwnerId) {
      toast.error("Nur der Board-Besitzer kann Aufgaben zuweisen");
      return;
    }

    if (assignedUsers.includes(userId)) {
      setAssignedUsers(assignedUsers.filter(id => id !== userId));
    } else {
      setAssignedUsers([...assignedUsers, userId]);
    }
  };

  const handleDelete = () => {
    if (window.confirm("Möchten Sie diese Karte wirklich löschen?")) {
      onDelete(card.id);
      onClose();
    }
  };

  const addLabel = () => {
    if (labelText.trim()) {
      setLabels([...labels, { text: labelText, color: labelColor }]);
      setLabelText("");
      setShowLabelInput(false);
    }
  };

  const removeLabel = (index) => {
    setLabels(labels.filter((_, idx) => idx !== index));
  };

  const createReminder = async () => {
    if (!reminderDate) {
      toast.error("Bitte wählen Sie ein Datum aus");
      return;
    }

    try {
      await axios.post("/reminders", {
        cardId: card.id,
        reminderDate: reminderDate.toISOString(),
        cardTitle: title,
      });
      toast.success("Erinnerung erstellt!");
      setReminderDate(null);
    } catch (error) {
      toast.error("Fehler beim Erstellen der Erinnerung");
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="card-modal">
        <DialogHeader>
          <DialogTitle>Karte bearbeiten</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="card-title">Titel</Label>
            <Input
              id="card-title"
              data-testid="card-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-lg font-medium"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="card-description">Beschreibung</Label>
            <Textarea
              id="card-description"
              data-testid="card-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Fügen Sie eine detaillierte Beschreibung hinzu..."
            />
          </div>

          {/* Labels */}
          <div className="space-y-2">
            <Label>Labels</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {labels.map((label, idx) => (
                <span
                  key={idx}
                  data-testid={`label-${idx}`}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: label.color }}
                >
                  {label.text}
                  <button
                    data-testid={`remove-label-${idx}`}
                    onClick={() => removeLabel(idx)}
                    className="hover:bg-black/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            {showLabelInput ? (
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-2">
                  <Input
                    data-testid="label-text-input"
                    placeholder="Label-Text"
                    value={labelText}
                    onChange={(e) => setLabelText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addLabel()}
                  />
                  <div className="flex gap-2">
                    {LABEL_COLORS.map((color) => (
                      <button
                        key={color}
                        data-testid={`label-color-${color}`}
                        onClick={() => setLabelColor(color)}
                        className={`w-8 h-8 rounded-full ${
                          labelColor === color ? "ring-2 ring-offset-2 ring-gray-900" : ""
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <Button data-testid="add-label-button" onClick={addLabel} size="sm">
                  Hinzufügen
                </Button>
                <Button onClick={() => setShowLabelInput(false)} variant="outline" size="sm">
                  Abbrechen
                </Button>
              </div>
            ) : (
              <Button
                data-testid="show-label-input"
                onClick={() => setShowLabelInput(true)}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Label hinzufügen
              </Button>
            )}
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Fälligkeitsdatum</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2" data-testid="due-date-trigger">
                  <Calendar className="w-4 h-4" />
                  {dueDate ? format(dueDate, "PPP", { locale: de }) : "Datum auswählen"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={dueDate}
                  onSelect={setDueDate}
                  locale={de}
                />
              </PopoverContent>
            </Popover>
            {dueDate && (
              <Button
                data-testid="clear-due-date"
                onClick={() => setDueDate(null)}
                variant="ghost"
                size="sm"
                className="text-red-600"
              >
                Datum entfernen
              </Button>
            )}
          </div>

          {/* Assigned Users */}
          <div className="space-y-2 border-t pt-4">
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Zugewiesene Benutzer
            </Label>
            {currentUserId !== boardOwnerId && (
              <p className="text-sm text-gray-500">
                Nur der Board-Besitzer kann Benutzer zuweisen
              </p>
            )}
            <div className="space-y-2">
              {boardMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-2 rounded-lg border hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    {member.avatarUrl ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${member.avatarUrl}`}
                        alt={member.name || member.username}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {(member.name || member.username).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium">{member.name || member.username}</p>
                      {member.email && (
                        <p className="text-xs text-gray-500">{member.email}</p>
                      )}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={assignedUsers.includes(member.id)}
                    onChange={() => toggleUserAssignment(member.id)}
                    disabled={currentUserId !== boardOwnerId}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              ))}
            </div>
            {assignedUsers.length > 0 && (
              <p className="text-sm text-green-600">
                {assignedUsers.length} Benutzer zugewiesen
              </p>
            )}
          </div>

          {/* Reminder */}
          <div className="space-y-2 border-t pt-4">
            <Label>Erinnerung</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1 justify-start gap-2" data-testid="reminder-date-trigger">
                    <Bell className="w-4 h-4" />
                    {reminderDate ? format(reminderDate, "PPP", { locale: de }) : "Erinnerungsdatum auswählen"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={reminderDate}
                    onSelect={setReminderDate}
                    locale={de}
                  />
                </PopoverContent>
              </Popover>
              <Button data-testid="create-reminder-button" onClick={createReminder} disabled={!reminderDate}>
                Erstellen
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button data-testid="delete-card-button" onClick={handleDelete} variant="destructive" className="gap-2">
              <Trash2 className="w-4 h-4" />
              Karte löschen
            </Button>
            <div className="flex gap-2">
              <Button onClick={onClose} variant="outline">
                Abbrechen
              </Button>
              <Button data-testid="save-card-button" onClick={handleSave} className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                Speichern
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
